import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Users,
  BookOpen,
  UserCheck,
} from "lucide-react";
import { getStudentById } from "../../services/students";
import { Student } from "../../types/student";

interface StudentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  onEdit?: () => void;
}

const StudentDetailsModal: React.FC<StudentDetailsModalProps> = ({
  isOpen,
  onClose,
  studentId,
  onEdit,
}) => {
  const {
    data: studentResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["student", studentId],
    queryFn: () => getStudentById(studentId),
    enabled: isOpen && !!studentId,
  });

  if (!isOpen) return null;

  const student = studentResponse?.data as Student;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Center the modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full sm:p-6 z-10">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <User className="h-6 w-6 text-blue-600" />
              Student Details
            </h3>
            <div className="flex items-center gap-2">
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                >
                  Edit
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              <div className="h-32 bg-gray-300 rounded"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-2">
                Error loading student details
              </div>
              <p className="text-gray-500 text-sm">Please try again later</p>
              <p className="text-gray-400 text-xs mt-2">
                Error: {error?.toString()}
              </p>
            </div>
          ) : !student || !studentResponse?.success ? (
            <div className="text-center py-8">
              <div className="text-yellow-500 mb-2">
                No student data available
              </div>
              <p className="text-gray-500 text-sm">
                Student information could not be loaded.
              </p>
              <p className="text-gray-400 text-xs mt-2">
                Response: {JSON.stringify(studentResponse)}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Basic Info Card */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {((student as any).photoUrl || (student as any).userId?.profileDetails?.profilePicture) ? (
                      <img
                        src={(student as any).photoUrl || (student as any).userId?.profileDetails?.profilePicture}
                        alt={`${(student as any).userId?.firstName} ${(student as any).userId?.lastName}`}
                        className="h-12 w-12 rounded-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center ${((student as any).photoUrl || (student as any).userId?.profileDetails?.profilePicture) ? 'hidden' : ''}`}>
                      <span className="text-blue-600 font-semibold text-lg">
                        {(student as any).userId?.firstName?.charAt(0) || ""}
                        {(student as any).userId?.lastName?.charAt(0) || ""}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">
                        {(student as any).userId?.firstName || ""}{" "}
                        {(student as any).userId?.lastName || ""}
                      </h4>
                      <p className="text-sm text-gray-500">
                        Student ID: {student.studentId || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        (student as any).academicStatus === "active"
                          ? "bg-green-100 text-green-800"
                          : (student as any).academicStatus === "graduated"
                          ? "bg-blue-100 text-blue-800"
                          : (student as any).academicStatus === "suspended"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {(student as any).academicStatus || "Active"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h5 className="text-md font-medium text-gray-900 border-b pb-2">
                    Personal Information
                  </h5>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-700">
                        <strong>Date of Birth:</strong>{" "}
                        {(student as any).userId?.profileDetails?.dateOfBirth
                          ? new Date(
                              (student as any).userId.profileDetails.dateOfBirth
                            ).toLocaleDateString()
                          : "Not provided"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-700">
                        <strong>Gender:</strong>{" "}
                        {(student as any).userId?.profileDetails?.gender
                          ? (student as any).userId.profileDetails.gender
                              .charAt(0)
                              .toUpperCase() +
                            (student as any).userId.profileDetails.gender.slice(
                              1
                            )
                          : "Not provided"}
                      </span>
                    </div>

                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-1" />
                      <span className="text-sm text-gray-700">
                        <strong>Address:</strong>
                        <br />
                        {(student as any).userId?.profileDetails?.address
                          ? `${
                              (student as any).userId.profileDetails.address
                                .street || ""
                            } ${
                              (student as any).userId.profileDetails.address
                                .city || ""
                            } ${
                              (student as any).userId.profileDetails.address
                                .state || ""
                            } ${
                              (student as any).userId.profileDetails.address
                                .zipCode || ""
                            }`.trim() || "Not provided"
                          : "Not provided"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h5 className="text-md font-medium text-gray-900 border-b pb-2">
                    Contact Information
                  </h5>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-700">
                        <strong>Phone:</strong>{" "}
                        {(student as any).userId?.profileDetails?.phone ||
                          "Not provided"}
                      </span>
                    </div>

                    {(student as any).userId?.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          <strong>Email:</strong>{" "}
                          {(student as any).userId.email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Academic Information */}
              <div className="space-y-4">
                <h5 className="text-md font-medium text-gray-900 border-b pb-2">
                  Academic Information
                </h5>

                <div className="space-y-4">
                  {/* Courses Enrolled */}
                  <div>
                    <h6 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <BookOpen className="h-4 w-4" />
                      Courses Enrolled ({(student as any).courses?.length || 0})
                    </h6>

                    {(student as any).courses &&
                    (student as any).courses.length > 0 ? (
                      <div className="space-y-3">
                        {(student as any).courses.map(
                          (course: any, index: number) => (
                            <div
                              key={index}
                              className="bg-gray-50 rounded-lg p-3"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900">
                                    {course.name || "No name"}
                                  </h4>
                                  {course.description && (
                                    <p className="text-xs text-gray-600 mt-1">
                                      {course.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        <BookOpen className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">No courses enrolled</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Parent/Guardian Information */}
              {(student as any).parentGuardianInfo && (
                <div className="space-y-4">
                  <h5 className="text-md font-medium text-gray-900 border-b pb-2">
                    Parent/Guardian Information
                  </h5>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          <strong>Emergency Contact:</strong>{" "}
                          {(student as any).parentGuardianInfo?.emergencyContact
                            ?.name || "Not provided"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          <strong>Relationship:</strong>{" "}
                          {(student as any).parentGuardianInfo?.emergencyContact
                            ?.relationship || "Not provided"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          <strong>Phone:</strong>{" "}
                          {(student as any).parentGuardianInfo?.emergencyContact
                            ?.phone || "Not provided"}
                        </span>
                      </div>

                      {(student as any).parentGuardianInfo?.emergencyContact
                        ?.alternatePhone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">
                            <strong>Alternate Phone:</strong>{" "}
                            {
                              (student as any).parentGuardianInfo
                                .emergencyContact.alternatePhone
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Academic Records */}
              {student.academicRecords &&
                student.academicRecords.length > 0 && (
                  <div className="space-y-4">
                    <h5 className="text-md font-medium text-gray-900 border-b pb-2">
                      Academic Records
                    </h5>

                    <div className="space-y-3">
                      {student.academicRecords.map((record, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <BookOpen className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-700">
                              {record.term}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                            {Object.entries(record.grades).map(
                              ([subject, grade]) => (
                                <span
                                  key={subject}
                                  className="bg-white px-2 py-1 rounded"
                                >
                                  {subject}: {grade}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Timestamps */}
              <div className="text-xs text-gray-500 border-t pt-4">
                <div className="flex justify-between">
                  <span>
                    Created: {new Date(student.createdAt).toLocaleString()}
                  </span>
                  <span>
                    Updated: {new Date(student.updatedAt).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end pt-6 border-t mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDetailsModal;
